import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link, Redirect } from 'react-router-dom';
import userStore from '../../../Stores/User';
import { observer } from 'mobx-react';
import { CircularProgress } from "@mui/material";
import store from "../../DiscoverNew/store";
import FeedSubscribed from "../../DiscoverNew/Components/FeedSubscribed";
import Player from "../../../Components/PlayerNew";
import mixpanel from "mixpanel-browser";

const Quickstart1 = observer((props) => {
  const [isArticleAdded, setIsArticleAdded] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const { 
    addArticleToPlaylist, 
    playlistArticlesToShow, 
    loadPlaylist, 
    unpausePlaylistUpdates, 
    allFeeds, 
    allFeedsLoading, 
    fetchAllFeeds,
    fetchUserFeeds,
    userFeeds,
    currentArticle, 
    isPlaying 
  } = store;
  const [visibleFeedsCount, setVisibleFeedsCount] = useState(12);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loaderRef = useRef(null);

  // Filter out feeds with placeholder audio URLs
  const validFeeds = allFeeds.filter(feed => !feed.audio_url?.includes('placeholder.mp3'));

  // Get subscribed feed IDs for filtering
  const subscribedFeedIds = new Set((userFeeds || []).map(feed => feed.feed_id));

  // Define feed categories based on content analysis
  const feedCategories = useMemo(() => {
    const categories = {
      all: { label: 'All Feeds', count: validFeeds.length },
      tech: { label: 'Technology', count: 0 },
      news: { label: 'News & Politics', count: 0 },
      business: { label: 'Business & Finance', count: 0 },
      science: { label: 'Science & Research', count: 0 },
      culture: { label: 'Culture & Arts', count: 0 }
    };

    // Count feeds in each category
    validFeeds.forEach(feed => {
      const title = (feed.title || '').toLowerCase();
      const desc = (feed.description || '').toLowerCase();
      const url = (feed.feed_id || '').toLowerCase();

      if (title.includes('tech') || desc.includes('tech') || url.includes('tech') ||
          title.includes('software') || desc.includes('software') ||
          title.includes('programming') || desc.includes('programming') ||
          title.includes('ai') || desc.includes('ai') ||
          title.includes('artificial intelligence') || desc.includes('artificial intelligence')) {
        categories.tech.count++;
      }
      if (title.includes('news') || desc.includes('news') || url.includes('news') ||
          title.includes('politics') || desc.includes('politics') ||
          title.includes('current events') || desc.includes('current events')) {
        categories.news.count++;
      }
      if (title.includes('business') || desc.includes('business') || url.includes('business') ||
          title.includes('finance') || desc.includes('finance') ||
          title.includes('economy') || desc.includes('economy') ||
          title.includes('market') || desc.includes('market')) {
        categories.business.count++;
      }
      if (title.includes('science') || desc.includes('science') || url.includes('science') ||
          title.includes('research') || desc.includes('research') ||
          title.includes('health') || desc.includes('health') ||
          title.includes('medicine') || desc.includes('medicine')) {
        categories.science.count++;
      }
      if (title.includes('culture') || desc.includes('culture') || url.includes('culture') ||
          title.includes('art') || desc.includes('art') ||
          title.includes('music') || desc.includes('music') ||
          title.includes('literature') || desc.includes('literature') ||
          title.includes('film') || desc.includes('film')) {
        categories.culture.count++;
      }
    });

    return categories;
  }, [validFeeds]);

  // Filter feeds based on selected category
  const filteredFeeds = useMemo(() => {
    if (selectedCategory === 'all') return validFeeds;

    return validFeeds.filter(feed => {
      const title = (feed.title || '').toLowerCase();
      const desc = (feed.description || '').toLowerCase();
      const url = (feed.feed_id || '').toLowerCase();

      switch (selectedCategory) {
        case 'tech':
          return title.includes('tech') || desc.includes('tech') || url.includes('tech') ||
                 title.includes('software') || desc.includes('software') ||
                 title.includes('programming') || desc.includes('programming') ||
                 title.includes('ai') || desc.includes('ai') ||
                 title.includes('artificial intelligence') || desc.includes('artificial intelligence');
        case 'news':
          return title.includes('news') || desc.includes('news') || url.includes('news') ||
                 title.includes('politics') || desc.includes('politics') ||
                 title.includes('current events') || desc.includes('current events');
        case 'business':
          return title.includes('business') || desc.includes('business') || url.includes('business') ||
                 title.includes('finance') || desc.includes('finance') ||
                 title.includes('economy') || desc.includes('economy') ||
                 title.includes('market') || desc.includes('market');
        case 'science':
          return title.includes('science') || desc.includes('science') || url.includes('science') ||
                 title.includes('research') || desc.includes('research') ||
                 title.includes('health') || desc.includes('health') ||
                 title.includes('medicine') || desc.includes('medicine');
        case 'culture':
          return title.includes('culture') || desc.includes('culture') || url.includes('culture') ||
                 title.includes('art') || desc.includes('art') ||
                 title.includes('music') || desc.includes('music') ||
                 title.includes('literature') || desc.includes('literature') ||
                 title.includes('film') || desc.includes('film');
        default:
          return true;
      }
    });
  }, [validFeeds, selectedCategory]);

  useEffect(() => {
    if (userStore.idToken) {
      fetchAllFeeds();
      fetchUserFeeds();
    }
  }, [userStore.idToken]);

  useEffect(() => {
    if (playlistArticlesToShow === null) {
      console.log("loading playlist")
    } else if (playlistArticlesToShow.length > 0) {
      console.log("playlist loaded")
      setIsArticleAdded(true);
    }
  }, [playlistArticlesToShow]);

  const handleSubscribe = async (feed) => {
    try {
      store.userFeeds.push(feed);
      await fetchUserFeeds(); // Refresh user feeds after subscribing
    } catch (error) {
      console.error('Error subscribing to feed:', error);
    }
  };

  const handleUnsubscribe = async (feed_id) => {
    try {
      store.userFeeds = store.userFeeds.filter(f => f.feed_id !== feed_id);
      await fetchUserFeeds(); // Refresh user feeds after unsubscribing
    } catch (error) {
      console.error('Error unsubscribing from feed:', error);
    }
  };

  const loadMoreFeeds = useCallback(() => {
    if (!isLoadingMore && validFeeds.length > visibleFeedsCount) {
      setIsLoadingMore(true);
      setTimeout(() => {
        setVisibleFeedsCount(prev => prev + 10);
        setIsLoadingMore(false);
      }, 300);
    }
  }, [isLoadingMore, visibleFeedsCount, validFeeds.length]);

  useEffect(() => {
    if (validFeeds.length <= visibleFeedsCount || !loaderRef.current) {
      return;
    }

    const options = {
      root: null,
      rootMargin: '200px 0px',
      threshold: 0.1,
    };

    const handleObserver = (entries) => {
      const [entry] = entries;
      if (entry.isIntersecting) {
        loadMoreFeeds();
      }
    };

    const observer = new IntersectionObserver(handleObserver, options);
    observer.observe(loaderRef.current);

    return () => {
      if (loaderRef.current) {
        observer.unobserve(loaderRef.current);
      }
    };
  }, [loadMoreFeeds, validFeeds.length, visibleFeedsCount]);

  if (isArticleAdded) {
    return <Redirect to="2"/>;
  }

  return (
    <div className="center-static" style={{ paddingBottom: currentArticle ? '100px' : '0' }}>
      <div style={{paddingTop: '50px', paddingBottom: '30px', textAlign: 'center'}}>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'center', 
          gap: '20px', 
          marginBottom: '20px', 
          marginTop: '30px',
          padding: '0 20px'
        }}>
          <h1 className="headline" style={{ 
            marginBottom: '0px', 
            fontSize: 'clamp(32px, 6vw, 48px)', 
            fontWeight: 'bold',
            lineHeight: '1.2'
          }}>
            Subscribe to some feeds
          </h1>
        </div>
        <div className="subheadline" style={{ 
          marginTop: '20px', 
          fontSize: 'clamp(18px, 4vw, 24px)',
          padding: '0 20px'
        }}>
          Click <span style={{
            background: '#4292fa',
            color: 'white',
            padding: '5px 15px',
            borderRadius: '5px',
            fontWeight: '500',
            display: 'inline-block',
            margin: '5px 0',
          }}>Subscribe</span> to add to your library
        </div>
        <p style={{ 
          marginTop: '10px', 
          opacity: 0.7,
          fontSize: 'clamp(14px, 3vw, 16px)',
          padding: '0 20px'
        }}>
          (This is just to get you started, you can unsubscribe later)
        </p>
        <div className="cta-div" style={{ 
          position: 'fixed',
          bottom: currentArticle ? '80px' : '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1000,
          width: '100%',
          maxWidth: '1200px',
          padding: '0 16px',
          pointerEvents: 'none'
        }}>
          <Link 
            to="/onboarding/quickstart/3" 
            style={{ 
              textDecoration: 'none',
              display: 'block',
              pointerEvents: 'auto',
              width: 'fit-content',
              margin: '0 auto'
            }}
            onClick={() => {
              const hasSubscribed = userFeeds.length > 1;
              mixpanel.track("Quickstart: Feeds setup done", {
                completed: hasSubscribed,
                feed_count: userFeeds.length
              });
              mixpanel.people.set_once({
                feeds_subscribed: hasSubscribed
              });
            }}
          >
            <button className="cta-button">
              <div>{userFeeds.length > 0 ? 'Next →' : 'Skip →'}</div>
            </button>
          </Link>
        </div>
      </div>

      <div style={{ width: '100%', maxWidth: '1200px', margin: '0 auto', padding: '0 16px' }}>
        {/* Category Filter */}
        <div style={{
          display: 'flex',
          gap: '10px',
          flexWrap: 'wrap',
          justifyContent: 'center',
          marginBottom: '30px',
          padding: '0 16px'
        }}>
          {Object.entries(feedCategories).map(([key, { label, count }]) => (
            <button
              key={key}
              onClick={() => setSelectedCategory(key)}
              style={{
                background: selectedCategory === key ? '#4292fa' : 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '20px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              {label}
              <span style={{
                background: selectedCategory === key ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '12px'
              }}>
                {count}
              </span>
            </button>
          ))}
        </div>

        {allFeedsLoading ? (
          <div style={{ 
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            padding: '50px 20px',
            gap: '20px'
          }}>
            <CircularProgress 
              size={40} 
              sx={{
                color: '#4292fa',
                '@keyframes gradient': {
                  '0%': { color: '#4292fa' },
                  '25%': { color: '#8535cd' },
                  '50%': { color: '#e82186' },
                  '75%': { color: '#ff5c57' },
                  '100%': { color: '#4292fa' }
                },
                animation: 'gradient 2s ease infinite'
              }}
            />
            <p style={{ 
              color: '#fff',
              fontSize: '16px',
              fontWeight: 500,
              background: 'linear-gradient(90deg, #4292fa, #8535cd, #e82186, #ff5c57)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundSize: '300% 100%',
              animation: 'gradient 2s ease infinite'
            }}>Loading feeds...</p>
          </div>
        ) : (
          <div
            style={{
              marginTop: 10,
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: '16px',
              width: '100%'
            }}
          >
            {filteredFeeds.slice(0, visibleFeedsCount).map((feed) => (
              <FeedSubscribed
                key={feed.feed_id}
                feed={feed}
                isSubscribed={subscribedFeedIds.has(feed.feed_id)}
                idToken={userStore.idToken}
                onSubscribe={() => handleSubscribe(feed)}
                onUnsubscribe={() => handleUnsubscribe(feed.feed_id)}
                selectArticle={(article) => store.changeCurrentArticleForcePlay(article)}
                isActive={currentArticle?.article_id === feed.article_id || currentArticle?.feed_id === feed.feed_id}
              />
            ))}
          </div>
        )}
      </div>
      {currentArticle && (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          backgroundColor: '#1a1a1a',
          borderTop: '1px solid #333',
        }}>
          <Player
            currentArticle={currentArticle}
            onPreviousArticle={store.onPreviousArticle}
            onNextArticle={store.onNextArticle}
            onArticleEnded={store.onArticleEnded}
            listeningWhere="onboarding"
            autoPlay={true}
            isFullPlayer={false}
            updateIsFullPlayer={() => {}}
            fullPlayerLeaveSpace={80}
          />
        </div>
      )}
    </div>
  );
});

export default Quickstart1;
